import { useId } from "react";

export type SparklineTone = "gold" | "vine" | "aubergine";

export interface SparklineProps {
  /** Suite de valeurs Y (CA mensuel, cols expédiés…). Le composant les
   *  normalise sur la hauteur du SVG, tu fournis juste les chiffres bruts. */
  points: number[];
  width?: number;
  height?: number;
  /** Couleur du trait + dégradé d'aire. Default : gold (cohérent charte). */
  tone?: SparklineTone;
  /** Affiche un point sur la dernière valeur (utile pour signaler "maintenant"). */
  showLastPoint?: boolean;
  /** Texte alternatif pour les lecteurs d'écran. Si fourni, le SVG devient
   *  porteur de sens (`role="img"` + aria-label). Sinon il reste décoratif
   *  (`aria-hidden`) — utiliser quand le sparkline accompagne un texte
   *  équivalent (ex : "+15 %", "12 mois glissants"). */
  ariaLabel?: string;
}

const TONE_HEX: Record<SparklineTone, string> = {
  gold: "#c89c2c",
  vine: "#4a7c3a",
  aubergine: "#5b3a5e",
};

/**
 * Mini graphique linéaire (sparkline) pour résumer une tendance sur n points.
 * Aucune axe, aucun label : juste la silhouette + une aire dégradée.
 * Pour des graphes plus riches → composant dédié (à venir : `LineChart`).
 */
export function Sparkline({
  points,
  width = 220,
  height = 56,
  tone = "gold",
  showLastPoint = true,
  ariaLabel,
}: SparklineProps) {
  // Identifiant unique pour le linearGradient (sinon collision si plusieurs
  // sparklines sur la même page). Hook appelé en TÊTE inconditionnellement
  // pour respecter les Rules of Hooks (sinon erreur "Rendered more hooks…"
  // au remount si points passe de [] à [valeurs]).
  const gradientId = useId();
  const safeId = `sparkGrad-${tone}-${gradientId.replace(/:/g, "")}`;

  // Garde-fou : sans points pas de courbe à dessiner.
  if (points.length < 2) {
    return <svg width={width} height={height} aria-hidden="true" />;
  }

  // Normalisation : on étire les points sur toute la largeur, et on inverse Y
  // (SVG a Y vers le bas → on veut max en haut).
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;

  const stepX = width / (points.length - 1);
  const padY = 4; // marge haute/basse pour que le trait ne touche pas les bords

  const coords = points.map((value, i) => {
    const x = i * stepX;
    const normalized = (value - min) / range;
    const y = height - padY - normalized * (height - padY * 2);
    return { x, y };
  });

  // Path du trait : M point0 L point1 L point2…
  const linePath = coords
    .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`)
    .join(" ");

  // Path de l'aire : trait + retour vers la base
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  const color = TONE_HEX[tone];
  // Existence garantie par l'early-return `points.length < 2` ci-dessus
  const last = coords[coords.length - 1]!;

  // Selon que le sparkline est porteur de sens (ariaLabel fourni) ou
  // purement décoratif (texte équivalent à proximité), on ajuste l'a11y.
  const a11yProps = ariaLabel
    ? { role: "img" as const, "aria-label": ariaLabel }
    : { "aria-hidden": true as const };

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: "block" }}
      {...a11yProps}
    >
      <defs>
        <linearGradient id={safeId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.30" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${safeId})`} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {showLastPoint && <circle cx={last.x} cy={last.y} r="3" fill={color} />}
    </svg>
  );
}
